/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermissionSet,
  BulkActionEntityType, BulkUsageResponse,
  ItemsGridFilter,
  UsageEntityType,
  UsageRestService,
  Usages,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  filterTracker,
  RouteDataService,
  SlicedDataSource,
} from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { ItemsListBusinessService } from '../../resources/items-list-business.service';
import { ItemsListItem } from '../../resources/items-list-item.model';
import { ItemsGridDefinition } from './items-grid-definition.service';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-items-list-tab',
  templateUrl: 'items-list-tab.component.html',
  styleUrls: ['items-list-tab.component.scss'],
  viewProviders: [ItemsGridDefinition],
})
export class ItemsListTabComponent  implements OnInit {
  dataSource: SlicedDataSource<ItemsListItem>;
  form: FilterableFormState<ItemsGridFilter>;
  cursor = newGridPaginationCursor();
  loadedData: ItemsListItem[] = [];
  activeLoadedData: ItemsListItem[] = [];
  checkList: any[] = [];
  get isShowDeleteSelected(): boolean {
    return !!this.checkList.length;
  }

  constructor(public gridDefinition: ItemsGridDefinition,
              private userContext: UserContext,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private waitingService: WaitingService,
              private formStateBuilder: FormStateBuilder,
              private navigationService: BuilderNavigationService,
              private businessService: ItemsListBusinessService,
              private commonActions: CommonActions,
              private pipelines: ListActionPipelines,
              private dialogService: DialogService,
              private notifications: NotificationService,
              private usageService: UsageRestService,
              private router: AppRouter) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout, itemIndex } = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      children: {
        searchString: void 0,
        showDeleted: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor, {
        defaultPageSize: gridLayout.pageSize,
        firstVisibleIndex: itemIndex,
      }));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.load());
  }

  load(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.form.submitValue);
  }

  onEdit(item: ItemsListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.Estimating.Items.EDIT_VIEW)) {
      this.router.navigateToChild([`/setup/estimating/items/${item.id}`]);
    }
  }

  onCreate(): void {
    this.router.navigateToChild([`/setup/estimating/items/new`]);
  }

  onDeactivate(item: ItemsListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_item_mark_as_deleted',
          params: {
            code: item.code,
          },
        },
        confirmNotUsed: {
          id: 'msg_item_delete',
          params: {
            code: item.code,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(item, state.value)),
      },
      finish: () => this.load(),
    });

    flow.run({
      value: item,
    });
  }

  onActivate(item: ItemsListItem): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_item_undelete',
        params: {
          code: item.code,
        },
      },
      execute: this.commonActions.execute((state: CommonFlowState<ItemsListItem>) =>
        this.businessService.activate(state.value)),
      finish: () => this.load(),
    });

    flow.run({
      value: item,
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId || void 0,
      entityType: entityType ? entityType : 'ITEM',
      label: label ? label : 'lbl_items',
    });
  }

  displayUsage(item: ItemsListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: item.id,
      entityType: UsageEntityType.ITEMS_USAGE_INFO,
    });
  }

  onDeleteSelected(): void {
    const entityList = this.loadedData
      .filter((model) => this.checkList.includes(model.id))
      ?.map((item) => ({id: item.id, version: item.version}));

    this.usageService.getBulkUsageInfo(<any[]>entityList, BulkActionEntityType.items)
      .pipe(this.waitingService.operator())
      .subscribe((res: BulkUsageResponse) => {
      this.dialogService.confirmation(
        yesNoConfirmation(res.allDeleted ? 'msg_items_delete_selected' : 'msg_items_delete_mark_as_deleted'),
      ).pipe(
        filter$(yesAnswer),
      ).subscribe(
          () => this.businessService.bulkDelete(res.usages.map((resItem: Usages) => ({
            id: resItem.itemId,
            usage: {
              version: resItem.version,
              deleted: resItem.deleted,
            },
          }))).subscribe(() => this.load(), this.notifications.processAll),
          this.notifications.processAll);
    },           this.notifications.processAll);
  }

  isActionButtonsVisible(): boolean {
    return !this.checkList.length;
  }

  onLoadData(event: any): void {
    this.checkList = [];
    this.loadedData = event.data;
    this.activeLoadedData = event.data.filter(this.canBeSelected);
  }

  rowClicked(event: any): void {
    if (event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.event.shiftKey) {
      this.onCheckChange(event.data.id);
      return;
    }
    if (this.isActionButtonsVisible()) {
      this.onEdit(event.data);
    }
  }

  onToggleSelectAll(): void {
    if (!this.checkList.length || (!!this.checkList.length && this.checkList.length < this.activeLoadedData.length)) {
      this.checkList = this.activeLoadedData?.map((item) => item.id);
    } else {
      this.checkList = [];
    }
  }

  onCheckChange(id: ID): void {
    const targetItem = this.loadedData.find((item) => item.id === id);
    if (!targetItem || !this.canBeSelected(targetItem)) {
      return;
    }
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      return;
    }
    this.checkList = [...this.checkList, id];
  }

  canBeSelected(item: ItemsListItem): boolean {
    return item.active;
  }
}
